import React, { useEffect, useMemo, useState } from 'react';
import { DataGridPro, koKR } from '@mui/x-data-grid-pro';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Radio,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { CustomNoRowsOverlay } from '../../datagrid';
import DocumentViewerDrawer from '../../common/viewer/DocumentViewerDrawer';
import * as subMatFolderActions from '../../../store/subMatFolder';
import {
  formatSubMatEndDate,
  formatSubMatExpiryStatusText,
  getSubMatExpiryStatus,
  getSubMatExpiryStatusTextColor,
} from '../../G04docuSubMatFolder/SubMatFolderExpiryStatus';

const toExplorerFolderPath = (folderPath) => {
  if (!folderPath) return '';
  return String(folderPath)
    .split(/\s*(?:›|\/)\s*/)
    .filter(Boolean)
    .join(' › ');
};

const rankCandidate = (file, row) => {
  let score = 0;
  if (row?.g04docuGCertificationId && file.specCertificationId === row.g04docuGCertificationId) {
    score += 4;
  }
  if (row?.productFolderId && file.specProductFolderId === row.productFolderId) {
    score += 4;
  }
  const code = String(row?.code || '').replace(/\s/g, '');
  const specCode = String(file.specCode || file.specThickness || '').replace(/\s/g, '');
  if (code && specCode && (specCode.includes(code) || code.includes(specCode))) {
    score += 2;
  }
  return score;
};

/**
 * 폴더형 대체성적서. 해당 회사 성적서 폴더의 현재 파일만 고른다.
 */
const FolderSubstitutePickerDialog = ({
  open,
  onClose,
  row,
  onDocumentSelected,
  onPreview,
}) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [keyword, setKeyword] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [previewUrl, setPreviewUrl] = useState('');
  const [selectedId, setSelectedId] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setKeyword('');
    setError('');
    setPreviewUrl('');
    setSelectedId('');
    setLoading(true);
    (async () => {
      try {
        const list = await subMatFolderActions.selectSubstituteCandidatesDirect({
          gclientId: row?.gclientId,
        });
        if (!cancelled) {
          setFiles(list || []);
          const current = (list || []).find((file) => file.folderId && file.folderId === row?.substituteFolderId);
          setSelectedId(current ? (current.id || current.folderId) : '');
        }
      } catch (e) {
        if (!cancelled) {
          setFiles([]);
          setError(e?.serverResponse?.message || e.message || '성적서 목록을 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, row?.gclientId, row?.substituteFolderId]);

  const rows = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return (files || [])
      .map((file) => ({
        ...file,
        folderPathLabel: toExplorerFolderPath(file.folderPath),
        endDateLabel: formatSubMatEndDate(file),
        expiryStatusLabel: formatSubMatExpiryStatusText(file),
      }))
      .filter((file) => !row?.division || file.specDivision === row.division)
      .filter((file) => {
        if (!q) return true;
        const hay = `${file.fileName || ''} ${file.folderPathLabel}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const diff = rankCandidate(b, row) - rankCandidate(a, row);
        if (diff !== 0) return diff;
        return String(a.folderPathLabel).localeCompare(String(b.folderPathLabel), 'ko');
      });
  }, [files, keyword, row]);

  const choose = (file) => {
    if (!onDocumentSelected || !file?.documentPath) return;
    const expired = getSubMatExpiryStatus(file) === 'expired';
    onDocumentSelected(row, {
      documentPath: file.documentPath,
      name: file.fileName || file.folderName || '대체성적서',
      documentType: '대체성적서',
      valid: !expired,
      isValid: !expired,
      isValidYN: file.validYN,
      gtestComparisonDocumentPath: file.comparisonDocumentPath || '',
      substituteFolderId: file.folderId,
      gclientId: row?.gclientId,
    });
  };

  const selectedFile = rows.find((file) => (file.id || file.folderId) === selectedId);

  const columns = useMemo(() => [
    {
      field: 'pick',
      headerName: '',
      width: 52,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const id = params.row.id || params.row.folderId;
        return (
          <Radio
            size="small"
            checked={selectedId === id}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onChange={() => setSelectedId(id)}
            inputProps={{ 'aria-label': params.row.fileName || '대체성적서' }}
          />
        );
      },
    },
    {
      field: 'fileName',
      headerName: '파일명',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'folderPathLabel',
      headerName: '폴더 경로',
      flex: 1.2,
      minWidth: 220,
      renderCell: (params) => {
        const path = params.value || '';
        if (!path) return '';
        const parts = path.split(' › ');
        return (
          <Tooltip title={path} followCursor>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                minWidth: 0,
                overflow: 'hidden',
                fontSize: 'inherit',
              }}
            >
              {parts.map((name, i) => (
                <Box
                  key={`${i}-${name}`}
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}
                >
                  {i > 0 && (
                    <Box component="span" sx={{ mx: 0.5, color: 'text.secondary', flexShrink: 0 }}>
                      ›
                    </Box>
                  )}
                  <Box
                    component="span"
                    sx={{
                      color: i === parts.length - 1 ? 'text.primary' : 'text.secondary',
                      fontWeight: i === parts.length - 1 ? 500 : 400,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {name}
                  </Box>
                </Box>
              ))}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'endDateLabel',
      headerName: '만료일',
      width: 120,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'expiryStatusLabel',
      headerName: '상태',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        const color = getSubMatExpiryStatusTextColor(params.row);
        return (
          <Box component="span" sx={color ? { color } : undefined}>
            {params.value}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '보기',
      width: 70,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="서류보기">
          <IconButton
            size="small"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              const path = onPreview?.(params.row.documentPath, row?.gclientId, row, { previewOnly: true });
              if (path) setPreviewUrl(path);
            }}
            sx={{ color: '#2EA2DE' }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [onPreview, row, selectedId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableEnforceFocus={Boolean(previewUrl)}>
      <DialogTitle>
        <Typography variant="h6">대체성적서 찾기</Typography>
        <Typography variant="body2" color="text.secondary">
          {row?.company}{row?.productName ? ` - ${row.productName}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          fullWidth
          placeholder="파일명, 폴더 경로"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          sx={{ mb: 1.5, mt: 0.5 }}
        />
        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>
        )}
        <Box sx={{ height: 420, width: '100%' }}>
          <DataGridPro
            localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
            sx={{ fontSize: '0.85em' }}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
              loadingOverlay: LinearProgress,
            }}
            headerHeight={38}
            rowHeight={34}
            loading={loading}
            getRowId={(file) => file.id || file.folderId}
            rows={rows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[10, 20, 50, 100]}
            pagination
            disableRowSelectionOnClick
            onRowClick={(params) => setSelectedId(params.row.id || params.row.folderId)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button
          variant="contained"
          disabled={!selectedFile?.documentPath}
          onClick={() => choose(selectedFile)}
        >
          선택
        </Button>
      </DialogActions>
      <DocumentViewerDrawer
        portal
        open={Boolean(previewUrl)}
        onClose={() => setPreviewUrl('')}
        url={previewUrl}
      />
    </Dialog>
  );
};

export default FolderSubstitutePickerDialog;
